import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';

// Module-level singleton — loads the Maps script exactly once
let googleMapsLoadState = 'idle';
const googleMapsReadyCallbacks = [];

function loadGoogleMapsScript(apiKey) {
  if (googleMapsLoadState === 'loaded') {
    googleMapsReadyCallbacks.forEach(cb => cb());
    googleMapsReadyCallbacks.length = 0;
    return;
  }
  if (googleMapsLoadState === 'loading') return;

  googleMapsLoadState = 'loading';
  window.__initGooglePlaces = () => {
    googleMapsLoadState = 'loaded';
    googleMapsReadyCallbacks.forEach(cb => cb());
    googleMapsReadyCallbacks.length = 0;
    delete window.__initGooglePlaces;
  };

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=__initGooglePlaces&loading=async`;
  script.async = true;
  script.defer = true;
  script.onerror = () => { googleMapsLoadState = 'error'; };
  document.head.appendChild(script);
}

const GooglePlacesAutocomplete = ({
  onAddressSelect,
  placeholder = "Enter your address",
  className = "",
  theme = {},
  googleApiKey,
  initialValue = ''
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(() => !!(window.google && window.google.maps));
  const [error, setError] = useState('');

  // The last value handed to onAddressSelect. A typed address is only worth
  // committing when it differs from what a suggestion already gave us —
  // otherwise clicking a suggestion and then leaving the field would overwrite
  // a full address, with coordinates, with the bare text sitting in the input.
  const committedRef = useRef(initialValue);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);

  // Always keep the ref pointing to the latest callback
  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  });

  // Sync inputValue when initialValue changes from the parent
  // (covers initial load in edit mode, post-save refresh, and venue clear)
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  // Load the Maps API script (singleton)
  useEffect(() => {
    if (!googleApiKey) { setError('Google API key is required'); return; }
    if (window.google?.maps) { setIsLoaded(true); return; }
    googleMapsReadyCallbacks.push(() => setIsLoaded(true));
    loadGoogleMapsScript(googleApiKey);
  }, [googleApiKey]);

  // Create a session token once the API is ready
  useEffect(() => {
    if (!isLoaded) return;
    (async () => {
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
      sessionTokenRef.current = new AutocompleteSessionToken();
    })();
  }, [isLoaded]);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    try {
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionTokenRef.current
      });
      setSuggestions(results || []);
      setIsOpen((results || []).length > 0);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
      setIsOpen(false);
    }
  }, [isLoaded]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setError('');

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  /**
   * Take the typed address as it stands.
   *
   * Google is not always there — the key can lapse, the script can fail, the
   * service can be slow or blocked — and until now the field was disabled
   * whenever that happened, so an academy or a listing simply could not be
   * created. A typed address with no coordinates is worth far more than a form
   * nobody can submit.
   *
   * Only the free-text fields are filled. Anything derived from a place —
   * city, state, country, latitude, longitude — is left empty rather than
   * guessed at, so distance search will not find these records and nothing
   * downstream mistakes a typed line for a geocoded one.
   */
  const commitTypedAddress = () => {
    const typed = inputValue.trim();
    if (!typed || typed === committedRef.current) return;
    committedRef.current = typed;
    onAddressSelectRef.current({
      fullAddress: typed,
      city: '', state: '', country: '', placeId: '', coordinates: null,
    });
  };

  const handleSelectSuggestion = async (suggestion) => {
    const placePrediction = suggestion.placePrediction;
    const displayText = placePrediction.text?.toString() || '';
    setInputValue(displayText);
    setSuggestions([]);
    setIsOpen(false);

    try {
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');

      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location', 'id', 'displayName']
      });

      // Refresh session token after each selection (billing best practice)
      sessionTokenRef.current = new AutocompleteSessionToken();

      const addressData = parseAddressComponents(place);

      if (!addressData.fullAddress) {
        setError('Could not retrieve address details. Please try a different selection.');
        return;
      }

      // Show the full formatted address in the input
      setInputValue(addressData.fullAddress || displayText);

      committedRef.current = addressData.fullAddress || displayText;
      onAddressSelectRef.current(addressData);
      setError('');
    } catch (err) {
      console.error('Error fetching place details:', err);
      setError('Failed to get place details. Please try again.');
    }
  };

  const parseAddressComponents = (place) => {
    const components = place.addressComponents || [];
    const displayName = place.displayName || '';
    const formattedAddress = place.formattedAddress || '';

    const addressData = {
      fullAddress: formattedAddress,
      placeId: place.id || '',
      placeName: displayName || formattedAddress,
      coordinates: {
        lat: place.location?.lat() || null,
        lng: place.location?.lng() || null
      },
      streetNumber: '',
      streetName: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      landmark: displayName && displayName !== formattedAddress ? displayName : ''
    };

    let cityComponent = null;
    let countyComponent = null;

    components.forEach(component => {
      const types = component.types || [];
      if (types.includes('street_number')) addressData.streetNumber = component.longText || '';
      if (types.includes('route')) addressData.streetName = component.longText || '';

      if (types.includes('locality')) {
        cityComponent = component;
      } else if (types.includes('administrative_area_level_2') && !cityComponent) {
        countyComponent = component;
      } else if (types.includes('sublocality_level_1') && !cityComponent && !countyComponent) {
        cityComponent = component;
      }

      if (types.includes('administrative_area_level_1')) addressData.state = component.shortText || '';
      if (types.includes('country')) addressData.country = component.longText || '';
      if (types.includes('postal_code')) addressData.zipCode = component.longText || '';
    });

    if (cityComponent) addressData.city = cityComponent.longText || '';
    else if (countyComponent) addressData.city = countyComponent.longText || '';

    addressData.addressLine1 = `${addressData.streetNumber} ${addressData.streetName}`.trim();
    return addressData;
  };

  if (!googleApiKey) {
    return <div className="text-red-500 text-sm">Google Places API key is required</div>;
  }

  return (
    <div className="relative">
      <MapPin
        className={`w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted || 'text-gray-400'} z-10 pointer-events-none`}
      />
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${theme.input || ''} placeholder:text-gray-400 ${className}`}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitTypedAddress(); } }}
        autoComplete="off"
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        onBlur={() => {
          // Delay close so onMouseDown on a suggestion fires first — and for
          // the same reason, delay the typed commit, or leaving the field to
          // click a suggestion would commit the half-typed text instead.
          debounceRef.current = setTimeout(() => {
            setIsOpen(false);
            commitTypedAddress();
          }, 200);
        }}
      />

      {!isLoaded && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10">
          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul className={`absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg border overflow-hidden ${theme.card || 'bg-white'} ${theme.border || 'border-gray-200'}`}>
          {suggestions.map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            const main = prediction.mainText?.toString() || prediction.text?.toString() || '';
            const secondary = prediction.secondaryText?.toString() || '';
            return (
              <li
                key={index}
                className={`flex items-start gap-2 px-4 py-3 cursor-pointer ${theme.cardHover || 'hover:bg-gray-50'} ${index < suggestions.length - 1 ? `border-b ${theme.border || 'border-gray-100'}` : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault(); // Keep focus in the input until we're done
                  handleSelectSuggestion(suggestion);
                }}
              >
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${theme.text || 'text-gray-900'}`}>{main}</p>
                  {secondary && (
                    <p className={`text-xs truncate ${theme.textMuted || 'text-gray-500'}`}>{secondary}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {error && <div className="mt-1 text-red-500 text-sm">{error}</div>}
      {!isLoaded && !error && (
        <div className="mt-1 text-gray-500 text-sm">Loading address autocomplete...</div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
